import {useNavigate, useParams} from 'react-router-dom';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IMediaTrack} from '../../../dto/MediaTrack.dto';
import {useRqWalkupRequestShowByToken} from '../../../react_query/WalkupRequest/RqWalkupRequestShow';
import {apiPost} from '../../../axiosConfig';
import {VerifyStudentToken} from '../../../react_query/music_selections/studentSelections/VerifyStudentToken';
import {AllowedPlayerTypeKeys} from './stepperComponents/SongMappingStepperCmpt';
import {INewWalkupPlayer} from './stepperComponents/WalkupLineupStepper';
import {WalkupMobileView} from './components/WalkupMobileView';
import {WalkupDesktopView} from './components/WalkupDesktopView';
import {useIsMobile} from './hooks/useIsMobile';
import {defaultApiState} from '../../../dto/Html.dto';
import DialogApiRequestStatus from '../../../components/dialogs/DialogApiRequestStatus';

const WalkupStudentSelectionDialog = () => {
  const {token} = useParams();
  const [formPlayersId, setformPlayersId] = useState([0]);
  const [players, setPlayers] = useState<INewWalkupPlayer[]>([]);
  const [playerId, setPlayerId] = useState<number>(0);
  const navigate = useNavigate();
  const [selectedPlayerType, setSelectedPlayerType] =
    useState<AllowedPlayerTypeKeys>('athlete');
  const songSelectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [apiState, setApiState] = useState(defaultApiState);

  const {data, isFetching} = useRqWalkupRequestShowByToken(token ?? '');

  const onSelect = useCallback(
    (track: IMediaTrack) => {
      setPlayers(prev => {
        if (playerId) {
          return prev.map(o => {
            if (o.id === playerId) {
              const previousDetailId = o[selectedPlayerType]?.id;
              return {
                ...o,
                [selectedPlayerType]: {
                  ...(previousDetailId ? {id: previousDetailId} : {}),
                  file: null,
                  media_track_id: track.id,
                  track,
                  track_artist: track.track_artist,
                  track_name: track.track_name,
                },
              };
            }
            return o;
          });
        }
        return prev;
      });
    },
    [playerId, selectedPlayerType, setPlayers],
  );

  const onDeSelect = useCallback(
    (_track: IMediaTrack) => {
      setPlayers(prev => {
        if (playerId) {
          return prev.map(o => {
            if (o.id === playerId) {
              delete o.track;
              const previousDetailId = o[selectedPlayerType]?.id;
              return {
                ...o,
                [selectedPlayerType]: {
                  ...(previousDetailId ? {id: previousDetailId} : {}),
                  file: null,
                  media_track_id: null,
                  track_artist: '',
                  track_name: '',
                },
              };
            }
            return o;
          });
        }
        return prev;
      });
    },
    [playerId, selectedPlayerType, setPlayers],
  );

  const track = useMemo(() => {
    const track = players.find(o => o.id === playerId)?.[
      selectedPlayerType as AllowedPlayerTypeKeys
    ]?.track;
    if (track) {
      return [track];
    }
    return [];
  }, [playerId, players, selectedPlayerType]);

  const isAthlete = useMemo(() => {
    if (!isFetching && data) {
      return data.players.some(
        p => p.athlete != null,
      );
    }
    return true;
  }, [data, isFetching]);

  useEffect(() => {
    if (!isFetching && data) {
      setformPlayersId(data.form_student_ids ?? []);
      setPlayers(
        data.players.map(val => {
          if (val.track) {
            return {...val, isPlayerDisabled: true};
          }
          return {...val, isPlayerDisabled: false};
        }) ?? [],
      );
    }
  }, [data, isFetching]);

  const CurrentFormStudents = useMemo(
    () => players.filter(val => formPlayersId.includes(val.id)),
    [formPlayersId, players],
  );

  const selectedPlayer = useMemo(() => {
    return CurrentFormStudents.find(p => p.id === playerId);
  }, [playerId, CurrentFormStudents]);

  const submit = useCallback(() => {
    setApiState({status: 'pending', error: null});
    const fd = new FormData();
    if (data?.name) {
      fd.append(`name`, data.name);
    }
    fd.append(`type`, data?.walkup_type ?? 'W');
    if (data?.event_id) {
      fd.append(`event_id`, data?.event_id.toString());
    }
    data?.stations?.map((val, key) => fd.set(`stations[${key}]`, `${val.id}`));

    if (players.length) {
      players.forEach((item, i) => {
        fd.set(`players[${i}][player_no]`, `${item.player_no}`);
        fd.set(`players[${i}][first_name]`, `${item.first_name}`);
        fd.set(`players[${i}][last_name]`, `${item.last_name}`);
        item?.player_type?.forEach((val, key) => {
          fd.set(`players[${i}][player_type][${key}]`, val);
          fd.set(`players[${i}][player_type][${key}]`, val);
        });

        item?.player_type?.forEach(val => {
          const type: AllowedPlayerTypeKeys =
            val.toLowerCase() as AllowedPlayerTypeKeys;
          fd.append(`players[${i}][id]`, `${item.id}`);
          if (item[type]?.id) {
            fd.append(`players[${i}][${type}][id]`, `${item[type]?.id}`);
          }
          if (item[type]?.track_artist) {
            fd.append(
              `players[${i}][${type}][track_artist]`,
              `${item[type]?.track_artist}`,
            );
          }
          if (item[type]?.track_name) {
            fd.append(
              `players[${i}][${type}][track_name]`,
              `${item[type]?.track_name}`,
            );
          }
          fd.set(
            `players[${i}][${type}][order]`,
            String(item[type]?.order ?? 0),
          );

          if (item[type]?.track?.id) {
            fd.append(
              `players[${i}][${type}][media_track_id]`,
              `${item[type]?.track?.id}`,
            );
          } else if (item[type]?.file) {
            fd.append(`players[${i}][${type}][file]`, item[type]?.file ?? '');
          }

          if (item.id > 0) {
            if (item[type]?.file) {
              fd.append(`players[${i}][${type}][type]`, `change`);
            } else {
              const value = data?.players.find(o => o.id === item.id);
              if (
                value?.[type]?.track?.id !== item[type]?.track?.id ||
                value?.player_no !== item.player_no ||
                value?.first_name !== item.first_name ||
                value?.last_name !== item.last_name ||
                value?.[type]?.order !== item[type]?.order
              ) {
                fd.append(`players[${i}][${type}][type]`, `change`);
              }
            }
          }
        });
      });
    }

    const submitUrl = `/v1/walkup-student-form/${token}`;
    apiPost(submitUrl, fd)
      .then(() => {
        setApiState({status: 'success', error: null});
        navigate(`/walkup-student-selection/${token}/success-msg`);
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [
    data?.name,
    data?.walkup_type,
    data?.event_id,
    data?.stations,
    data?.players,
    players,
    token,
    navigate,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      const verifyStatus = await VerifyStudentToken(token ?? '', 'walkup');
      if (verifyStatus !== 200)
        navigate(`/walkup-student-selection/${token}/error-msg`);
    };
    fetchData();
  }, [navigate, token]);

  useEffect(() => {
    if (playerId && songSelectionRef.current) {
      songSelectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [playerId]);

  const handleDialogCancel = () => {
    setApiState(defaultApiState);
  };

  return (
    <>
      <WalkupMobileView
        isVisible={isMobile}
        playerId={playerId}
        setPlayerId={setPlayerId}
        selectedPlayerType={selectedPlayerType}
        setSelectedPlayerType={setSelectedPlayerType}
        currentFormStudents={CurrentFormStudents}
        selectedPlayer={selectedPlayer}
        track={track as IMediaTrack[]}
        onSelect={onSelect}
        onDeSelect={onDeSelect}
        submit={submit}
        isAthlete={isAthlete}
        songSelectionRef={songSelectionRef}
      />
      <WalkupDesktopView
        isVisible={!isMobile}
        playerId={playerId}
        setPlayerId={setPlayerId}
        selectedPlayerType={selectedPlayerType}
        setSelectedPlayerType={setSelectedPlayerType}
        currentFormStudents={CurrentFormStudents}
        track={track as IMediaTrack[]}
        onSelect={onSelect}
        onDeSelect={onDeSelect}
        submit={submit}
        isAthlete={isAthlete}
      />
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          onEdit={handleDialogCancel}
          onRetry={submit}
          onClose={handleDialogCancel}
          apiState={apiState}
        />
      )}
    </>
  );
};

export default WalkupStudentSelectionDialog;
